import { Link } from 'react-router-dom';
import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import DimensionLine from '@/components/DimensionLine';

export const meta = {
  path: '/kwc-guelph-commercial-owner-brief',
  title: 'KWC & Guelph Commercial Owner Brief — September 2026',
  description:
    'A September 2026 briefing for commercial property and business owners in Kitchener, Waterloo, Cambridge and Guelph: financing conditions, planning changes, zoning diligence and owner action items.',
};

const SOURCE_DATE = 'September 4, 2026';

const actions = [
  {
    n: '01',
    title: 'Re-run your debt and value assumptions',
    body: 'The Bank of Canada held its overnight target at 2.25% on September 2. That is a useful financing benchmark, but it is not a commercial mortgage quote or a property cap rate. Owners considering a refinance or sale should update actual lender terms, debt coverage and valuation assumptions rather than relying on an old rate environment.',
  },
  {
    n: '02',
    title: 'Check land-use rules before promising a use',
    body: 'Planning rules are moving in several local municipalities. A buyer or tenant may value a property very differently depending on whether the intended use is permitted, needs a zoning certificate, requires a variance or sits inside a special planning framework.',
  },
  {
    n: '03',
    title: 'Prepare the lease file before going to market',
    body: 'For tenanted assets, organize executed leases, amendments, rent roll, options, recoveries, arrears history and material landlord obligations before buyers start underwriting the income stream.',
  },
  {
    n: '04',
    title: 'Separate asking-rent math from occupancy cost',
    body: 'For leases, base rent is only part of the economics. Owners and occupiers should model additional rent / TMI, area, free-rent periods and lease term together before comparing alternatives.',
  },
];

export default function KWCGuelphCommercialOwnerBrief() {
  useTitle(meta.title, meta.description);
  useJsonLd('article-kwc-guelph-commercial-owner-brief', {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'KWC & Guelph Commercial Owner Brief — September 2026',
    description: meta.description,
    datePublished: '2026-09-04',
    dateModified: '2026-09-04',
    author: { '@type': 'Person', name: site.agent.name },
    publisher: { '@type': 'Organization', name: site.brand.name },
    mainEntityOfPage: `https://pinnaclekwc.com${meta.path}`,
    about: ['Commercial real estate', 'Kitchener', 'Waterloo', 'Cambridge', 'Guelph', 'Ontario'],
  });

  return (
    <>
      <section className="border-b border-medline bg-grid-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <p className="annot">Owner brief — Kitchener · Waterloo · Cambridge · Guelph</p>
            <span className="font-mono text-[10px] uppercase tracking-wider2 text-medline">Updated {SOURCE_DATE}</span>
          </div>
          <h1 className="mt-4 max-w-5xl font-display text-[38px] font-semibold leading-[1.03] tracking-tight sm:text-[58px]">
            Commercial owners: four things worth checking this month.
          </h1>
          <p className="mt-6 max-w-3xl text-[17px] leading-relaxed text-annot">
            A practical September 2026 briefing for local property and business owners. No invented cap rates, no generic market-score claims — just current public developments and the owner decisions they can affect.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/commercial-property-value-calculator" className="btn-primary">Run the NOI value calculator</Link>
            <Link to="/contact?interest=buying-selling" className="btn-ghost">Discuss a property or business</Link>
          </div>
        </div>
      </section>

      <section className="bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="September owner actions" className="mb-10" />
          <div className="grid gap-px border border-medline bg-medline md:grid-cols-2">
            {actions.map((item) => (
              <article key={item.n} className="bg-sheet px-6 py-7 sm:px-8">
                <p className="sheet-no">{item.n}</p>
                <h2 className="mt-3 font-display text-[24px] leading-tight">{item.title}</h2>
                <p className="mt-4 text-[15px] leading-relaxed text-graphite-600">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-table">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Financing benchmark" className="mb-10" />
          <div className="grid gap-10 lg:grid-cols-[0.72fr_1.28fr]">
            <div>
              <p className="font-display text-[62px] font-semibold leading-none tracking-tight">2.25%</p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-wider2 text-annot">Bank of Canada overnight target · Sept. 2, 2026</p>
            </div>
            <div className="space-y-5 text-[16px] leading-relaxed text-graphite-600">
              <p>
                The Bank of Canada maintained its target for the overnight rate at 2.25% on September 2, 2026. The Bank also said uncertainty remains elevated and noted increased upside inflation risks alongside trade uncertainty.
              </p>
              <p>
                For a commercial owner, the useful takeaway is not “values should be X” or “commercial mortgage rates are Y.” Property financing depends on lender pricing, term, amortization, leverage, debt-service coverage, borrower strength and asset risk. Use the policy rate as context, then obtain property-specific financing assumptions.
              </p>
              <a href="https://www.bankofcanada.ca/2026/09/fad-press-release-2026-09-02/" target="_blank" rel="noreferrer" className="inline-block text-[13px] text-redline hover:text-redline-600">
                Source: Bank of Canada — September 2 rate decision
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Local planning watch" className="mb-10" />
          <div className="grid gap-8 lg:grid-cols-3">
            <article className="border-t-2 border-redline pt-5">
              <p className="sheet-no">KITCHENER</p>
              <h2 className="mt-3 font-display text-[23px] leading-tight">A new Official Plan has been adopted — but is not yet in force.</h2>
              <p className="mt-4 text-[14px] leading-relaxed text-graphite-600">
                Kitchener Council adopted a new Official Plan on June 29, 2026. The City says it is with the Ministry of Municipal Affairs and Housing for final approval and will only come into effect after approval. Owners evaluating redevelopment potential should distinguish between the adopted future framework and the rules currently in force.
              </p>
              <a href="https://www.kitchener.ca/development-and-construction/official-plan/" target="_blank" rel="noreferrer" className="mt-4 inline-block text-[13px] text-redline hover:text-redline-600">City of Kitchener source</a>
            </article>

            <article className="border-t-2 border-redline pt-5">
              <p className="sheet-no">WATERLOO</p>
              <h2 className="mt-3 font-display text-[23px] leading-tight">The Erb Street corridor now has a separate planning-permit framework.</h2>
              <p className="mt-4 text-[14px] leading-relaxed text-graphite-600">
                Waterloo says its Community Planning Permit Bylaw for the Erb Street corridor was passed May 11, 2026. In that defined corridor, the usual Zoning Bylaw does not apply in the same way; the CPP Bylaw regulates land use, building location and parking. That is material diligence for owners, buyers and tenants assessing a site in the corridor.
              </p>
              <a href="https://www.waterloo.ca/planning-and-development/check-zoning-and-land-use-rules/zoning-bylaw-and-map/" target="_blank" rel="noreferrer" className="mt-4 inline-block text-[13px] text-redline hover:text-redline-600">City of Waterloo source</a>
            </article>

            <article className="border-t-2 border-redline pt-5">
              <p className="sheet-no">GUELPH</p>
              <h2 className="mt-3 font-display text-[23px] leading-tight">Parts of the newer zoning framework still require appeal-aware diligence.</h2>
              <p className="mt-4 text-[14px] leading-relaxed text-graphite-600">
                Guelph says its 2023 Comprehensive Zoning Bylaw is largely in force, with certain appealed provisions and a partial settlement reached in March 2026. For a commercial or industrial property, confirm the exact zone, site-specific provisions and whether any appealed regulation affects the proposed use or development concept.
              </p>
              <a href="https://guelph.ca/city-government/by-laws-and-policies/zoning-by-law/" target="_blank" rel="noreferrer" className="mt-4 inline-block text-[13px] text-redline hover:text-redline-600">City of Guelph source</a>
            </article>
          </div>

          <div className="mt-10 border-l-2 border-redline bg-redline-100/40 px-6 py-5">
            <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Cambridge note</p>
            <p className="mt-2 max-w-4xl text-[14px] leading-relaxed text-graphite-600">
              Cambridge's Phase One Comprehensive Zoning By-law 26-007 came into effect February 3, 2026 and applies to certain residential lands. The City says its older Zoning By-law 150-85 continues to regulate other land uses. For a commercial property, do not assume the new residential framework replaced the commercial rules — check the property itself.
            </p>
            <a href="https://www.cambridge.ca/business-building-development/building-planning/zoning/" target="_blank" rel="noreferrer" className="mt-3 inline-block text-[13px] text-redline hover:text-redline-600">City of Cambridge source</a>
          </div>
        </div>
      </section>

      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Owner decision desk" className="mb-10" />
          <div className="grid gap-6 md:grid-cols-3">
            <div className="border border-medline p-6">
              <p className="sheet-no">VALUE</p>
              <h2 className="mt-3 font-display text-[22px]">What does your NOI imply?</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">Test your own NOI and cap-rate assumption without pretending one cap rate fits every asset.</p>
              <Link to="/commercial-property-value-calculator" className="mt-5 inline-block text-[13px] text-redline hover:text-redline-600">Commercial property value calculator →</Link>
            </div>
            <div className="border border-medline p-6">
              <p className="sheet-no">LEASE</p>
              <h2 className="mt-3 font-display text-[22px]">What is the actual occupancy math?</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">Model base rent, additional rent / TMI, square footage, term and free-rent periods together.</p>
              <Link to="/commercial-lease-nnn-rent-calculator" className="mt-5 inline-block text-[13px] text-redline hover:text-redline-600">Commercial lease / NNN calculator →</Link>
            </div>
            <div className="border border-medline p-6">
              <p className="sheet-no">SELL</p>
              <h2 className="mt-3 font-display text-[22px]">Is the property sale-ready?</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">For tenanted assets, build the lease and operating-document file before the buyer starts diligence.</p>
              <Link to="/selling-tenanted-commercial-property-ontario" className="mt-5 inline-block text-[13px] text-redline hover:text-redline-600">Tenanted-property owner guide →</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-table">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div>
              <p className="annot">Pinnacle Commercial · Owner brief</p>
              <h2 className="mt-2 max-w-3xl font-display text-[32px] leading-tight sm:text-[40px]">Have a property, lease or business decision coming up?</h2>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-annot">
                Bring the actual property facts. The discussion can start with the lease, zoning, NOI, seller timing or business real-estate question — not a generic sales pitch.
              </p>
            </div>
            <Link to="/contact" className="btn-primary">Talk with {site.agent.shortName}</Link>
          </div>
        </div>
      </section>
    </>
  );
}
